import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import { useStopInfo } from '../models/useStopInfo';
import { LoadingBackdrop } from '@components/LoadingBackdrop/LoadingBackdrop';
import { EntitiesListRow } from '@features/entities/entities-list/ui/EntitiesListRow/EntitiesListRow';
import '../transit-stop.scss';
import { RouteEntity } from '@features/public-transit-editor/transit-management/TransitStops/models/types';

const RouteIndexBadge = memo(({ index, color }: { index?: number; color?: string }) => {
    return (
        <div
            className="transit-stop-info__route-index"
            style={{ backgroundColor: color || '#ccc' }}
        >
            {index ?? '?'}
        </div>
    );
});

const RouteIndexRowForm = memo(({ entity }: { entity: RouteEntity }) => {
    return (
        <div className="transit-stop-info__route-form">
            <RouteIndexBadge index={entity.index} color={entity.color} />
            <div className="transit-stop-info__route-name">{entity.name ?? entity.id}</div>
            {entity.departures ? (
                <Tooltip title="departures" placement="top" disableInteractive>
                    <div className="transit-stop-info__route-departures">{entity.departures}</div>
                </Tooltip>
            ) : null}
        </div>
    );
});

const TransitStopInfo = memo(() => {
    const { loading, stop, routes, schedule, onRouteClick } = useStopInfo();
    const { t } = useTranslation();

    return (
        <div className="transit-stop-info">
            <LoadingBackdrop isVisible={loading} transparent />
            {!loading && stop && (
                <>
                    <div className="transit-stop-info__header">
                        <div className="transit-stop-info__title">{stop.name || stop.id}</div>
                    </div>

                    <div className="transit-stop-info__section">
                        <div className="transit-stop-info__section-title">
                            {t('transit-lines.stop-routes')}
                        </div>
                        <div className="transit-stop-info__list">
                            {routes.map(route => (
                                <EntitiesListRow
                                    key={route.id}
                                    entity={route}
                                    form={RouteIndexRowForm}
                                    onRowClick={onRouteClick}
                                    deleteFn={async () => {
                                        return;
                                    }}
                                    hasLink={false}
                                    hideMenuActions={true}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="transit-stop-info__section">
                        <div className="transit-stop-info__section-title">
                            {t('transit-lines.stop-departures')}
                        </div>
                        <div className="transit-stop-info__list">
                            {schedule.map((item, idx) => (
                                <div key={idx} className="transit-stop-info__schedule-row">
                                    <div className="transit-stop-info__schedule-time">
                                        {item.time}
                                    </div>
                                    <div className="transit-stop-info__schedule-route">
                                        <RouteIndexBadge
                                            index={item.routeIndex}
                                            color={item.color}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
});

export default TransitStopInfo;
